import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { assessments, onboarding } from '../utils/api';
import { toast } from 'sonner';
import GeneratingTalentCardScreen from '../components/GeneratingTalentCardScreen';
import PageFooter from '../components/PageFooter';
import { 
    Briefcase, ArrowLeft, ArrowRight, CheckCircle, Sparkles, 
    User, Target, Brain, Heart, Trophy, Clock, MapPin, ChevronUp, ChevronDown,
    Shield, AlertTriangle, Calendar, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOTAL_STEPS = 9;

// Block choice options
const PROBLEM_TYPE_OPTIONS = [
    "People", "Sales", "Operations", "Logistics",
    "Systems / Data", "Creative", "Strategy",
    "Unclear / Ambiguous problems"
];

const ENVIRONMENT_OPTIONS = [
    "Clear goals and autonomy",
    "Fast-paced",
    "Structured and predictable",
    "Collaborative / social",
    "Deep focus / independent",
    "Customer-facing",
    "Mission-driven"
];

const TEAM_OPTIONS = [
    "Small and tight-knit",
    "Large and specialised",
    "Mostly independent",
    "Highly collaborative",
    "Mixed / situational"
];

const MANAGER_OPTIONS = [
    "Clear direction and check-ins",
    "Trusts me with autonomy",
    "Coaching-oriented",
    "Direct and outcome-focused",
    "Hands-off unless needed"
];

const DEADLINE_OPTIONS = [
    "Motivating — I work better with them",
    "Stressful — but I still deliver",
    "Helpful guardrails",
    "I prefer setting my own pace",
    "Depends on the context",
    "Irrelevant — I focus on quality regardless",
    "Fine when realistic, frustrating when not"
];

const INSTRUCTION_OPTIONS = [
    "Clear instructions — I like knowing what's expected",
    "Loose direction — I figure things out best on my own",
    "A mix — depends on how familiar the task is"
];

const AssessmentWizard = () => {
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [apiComplete, setApiComplete] = useState(false);
    const [skillsList, setSkillsList] = useState([]);
    
    // Sector/role selection
    const [sectors, setSectors] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [allRoles, setAllRoles] = useState([]);
    const [roleSearchQuery, setRoleSearchQuery] = useState('');
    const [browseBySector, setBrowseBySector] = useState(false);
    const [browseSelectedSector, setBrowseSelectedSector] = useState(null);
    
    // Custom skill input
    const [customSkillInput, setCustomSkillInput] = useState('');
    
    // Microtest session state
    const [microtestSession, setMicrotestSession] = useState(null);
    const [microtestQuestions, setMicrotestQuestions] = useState([]);
    const [currentMicrotestIndex, setCurrentMicrotestIndex] = useState(0);
    const [microtestAnswers, setMicrotestAnswers] = useState({});
    const [showAntiCheatModal, setShowAntiCheatModal] = useState(false);
    const [antiCheatAcknowledged, setAntiCheatAcknowledged] = useState(false);
    const [graceUsed, setGraceUsed] = useState(false);
    const [microtestLoading, setMicrotestLoading] = useState(false);
    const [microtestError, setMicrotestError] = useState(null);
    
    const [formData, setFormData] = useState({
        header: {
            current_role: { title: '', company: '', years: '', status: '' },
            primary_role: '',
            primary_role_id: '',
            secondary_role: '',
            secondary_role_id: '',
            location: '',
            work_preference: '',
            availability_start: '',
            profile_photo_url: null
        },
        skills: {
            top_skills: [],
            custom_skills: [],
            skill_example_1: '',
            skill_example_2: '',
            skill_improvements: ''
        },
        problem_solving: {
            q1_problem_types: [],
            q2_figured_out: '',
            q10_risky_decision: ''
        },
        communication: {
            q1_explain_to_others: '',
            q2_input_vs_independent: ''
        },
        working_style: {
            q1_environments: [],
            q2_team: [],
            q3_manager: [],
            q8_deadlines: '',
            q6_instructions: '',
            q11_next_role: ''
        },
        achievements: {
            achievement_1: { headline: '', description: '', evidence: '' },
            achievement_2: { headline: '', description: '', evidence: '' },
            achievement_3: { headline: '', description: '', evidence: '' }
        },
        trial_readiness: {
            trial_length: '',
            availability: '',
            work_location: '',
            start_date: '',
            pay_expectations: '',
            salary_format: 'annual',
            constraints: '',
            flexibility_notes: ''
        },
        sector: '',
        micro_test_response: {}
    });

    useEffect(() => {
        loadSkills();
        loadSectors();
        // Onboarding funnel tracking (admin-only) — fire-and-forget, never
        // awaited, never blocks the wizard. Step 1 ("Your Profile") is the
        // first screen shown, so wizard mount == details_started.
        onboarding.track('details_started');
        // Safety net: if a candidate's Talent Card actually finished
        // generating on the backend (e.g. during a long AI wait) but their
        // page got reloaded before the automatic/manual continue happened,
        // don't strand them back at Step 1 — send them straight to their
        // already-ready dashboard instead.
        assessments.getStatus().then(res => {
            if (res.data?.completed) {
                navigate('/candidate/dashboard', { state: { showTalentCard: true } });
            }
        }).catch(() => {});
    }, []);
    
    useEffect(() => {
        if (formData.sector) {
            loadRolesForSector(formData.sector);
        } else {
            setAvailableRoles([]);
        }
    }, [formData.sector]);
    
    // Anti-cheat: Monitor visibility changes during microtest
    useEffect(() => {
        if (step !== 9 || !microtestSession || microtestSession.status !== 'in_progress') return;
        
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                handleViolation('visibility_hidden');
            }
        };
        
        const handleBlur = () => {
            handleViolation('app_blur');
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleBlur);
        };
    }, [step, microtestSession, graceUsed]);

    // Onboarding funnel tracking (admin-only) — fire-and-forget. Fires once
    // per question_number reached inside the Step 9 microtest (dedup is
    // handled server-side, so Previous/Next navigation never double-counts).
    useEffect(() => {
        if (step === 9 && microtestQuestions.length > 0) {
            onboarding.track('question_reached', currentMicrotestIndex + 1);
        }
    }, [step, currentMicrotestIndex, microtestQuestions.length]);
    
    const loadSectors = async () => {
        try {
            const response = await fetch(`${API_URL}/api/microtests/sectors`);
            const data = await response.json();
            const sectorList = data.sectors || [];
            setSectors(sectorList);
            if (sectorList.length > 0) loadAllRoles(sectorList);
        } catch (error) {
            console.error('Error loading sectors:', error);
        }
    };

    const loadAllRoles = async (sectorList) => {
        try {
            const results = await Promise.all(
                sectorList.map(s =>
                    fetch(`${API_URL}/api/microtests/sectors/${s.id}/roles`)
                        .then(r => r.json())
                        .then(d => (d.roles || []).map(r => ({ ...r, sector_id: s.id, sector_name: s.name })))
                        .catch(() => [])
                )
            );
            setAllRoles(results.flat());
        } catch (error) {
            console.error('Error loading all roles:', error);
        }
    };

    const filteredRoles = useMemo(() => {
        const q = roleSearchQuery.trim().toLowerCase();
        if (!q) return [];
        return allRoles.filter(r => r.name.toLowerCase().includes(q)).slice(0, 50);
    }, [allRoles, roleSearchQuery]);

    const selectRoleAndSector = (role) => {
        setFormData(prev => ({
            ...prev,
            sector: role.sector_id,
            header: {
                ...prev.header,
                primary_role_id: role.id,
                primary_role: role.name,
                secondary_role_id: '',
                secondary_role: ''
            }
        }));
        setRoleSearchQuery('');
        setBrowseSelectedSector(null);
    };
    
    const loadRolesForSector = async (sectorId) => {
        setLoadingRoles(true);
        try {
            const response = await fetch(`${API_URL}/api/microtests/sectors/${sectorId}/roles`);
            const data = await response.json();
            setAvailableRoles(data.roles || []);
        } catch (error) {
            console.error('Error loading roles:', error);
            setAvailableRoles([]);
        }
        setLoadingRoles(false);
    };
    
    const initializeMicrotestSession = async (sectorId, roleId, authToken) => {
        setMicrotestLoading(true);
        setMicrotestError(null);
        try {
            const response = await fetch(`${API_URL}/api/microtests/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                credentials: 'include',
                body: JSON.stringify({
                    sector_id: sectorId,
                    primary_role_id: roleId,
                    secondary_role_id: formData.header.secondary_role_id || null
                })
            });
            const data = await response.json();
            
            if (!response.ok) {
                setMicrotestError(data.detail || 'Failed to start microtest');
                setMicrotestLoading(false);
                return;
            }
            
            setMicrotestSession(data);
            const questions = [
                ...(data.primary_role?.general_microtests || []),
                ...(data.primary_role?.role_specific_microtests || [])
            ];
            
            if (questions.length === 0) {
                setMicrotestError('No microtest questions found for this role.');
                setMicrotestLoading(false);
                return;
            }
            
            setMicrotestQuestions(questions);
            setCurrentMicrotestIndex(0);
            setMicrotestAnswers({});
            setShowAntiCheatModal(true);
        } catch (error) {
            setMicrotestError('Network error loading microtest. Please check your connection and try again.');
        } finally {
            setMicrotestLoading(false);
        }
    };
    
    const handleAcknowledgeAntiCheat = async () => {
        if (!microtestSession) return;
        try {
            await fetch(`${API_URL}/api/microtests/acknowledge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                credentials: 'include',
                body: JSON.stringify({ session_id: microtestSession.session_id })
            });
            setMicrotestSession(prev => ({ ...prev, status: 'in_progress' }));
            setAntiCheatAcknowledged(true);
            setShowAntiCheatModal(false);
        } catch (error) {
            console.error('Error acknowledging rules:', error);
            toast.error('Failed to start microtest. Please try again.');
        }
    };
    
    const handleViolation = async (violationType) => {
        if (!microtestSession) return;
        if (!graceUsed) {
            setGraceUsed(true);
            toast.warning('Warning: Tab switch detected. One more violation will restart the test.', { duration: 5000 });
            try {
                await fetch(`${API_URL}/api/microtests/violation`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    credentials: 'include',
                    body: JSON.stringify({ session_id: microtestSession.session_id, violation_type: violationType })
                });
            } catch (error) { console.error('Error recording violation:', error); }
            return;
        }
        try {
            const restartResponse = await fetch(`${API_URL}/api/microtests/restart`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                credentials: 'include',
                body: JSON.stringify({
                    sector_id: formData.sector,
                    primary_role_id: formData.header.primary_role_id,
                    secondary_role_id: formData.header.secondary_role_id || null
                })
            });
            const data = await restartResponse.json();
            setMicrotestSession(data);
            const questions = [
                ...(data.primary_role?.general_microtests || []),
                ...(data.primary_role?.role_specific_microtests || [])
            ];
            setMicrotestQuestions(questions);
            setCurrentMicrotestIndex(0);
            setMicrotestAnswers({});
            setGraceUsed(false);
            setAntiCheatAcknowledged(false);
            setShowAntiCheatModal(true);
            toast.error('Session invalidated. Microtest restarting with new questions.');
        } catch (error) { console.error('Error restarting session:', error); }
    };
    
    const submitMicrotestAnswer = async (questionId, answer) => {
        if (!microtestSession) return;
        try {
            await fetch(`${API_URL}/api/microtests/answer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                credentials: 'include',
                body: JSON.stringify({ session_id: microtestSession.session_id, question_id: questionId, answer, role_type: 'primary' })
            });
            setMicrotestAnswers(prev => ({ ...prev, [questionId]: answer }));
        } catch (error) { console.error('Error submitting answer:', error); }
    };
    
    const completeMicrotestSession = async () => {
        if (!microtestSession) return;
        try {
            await fetch(`${API_URL}/api/microtests/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                credentials: 'include',
                body: JSON.stringify({ session_id: microtestSession.session_id })
            });
        } catch (error) { console.error('Error completing microtest:', error); }
    };

    const loadSkills = async () => {
        try {
            const res = await assessments.getSkillsList();
            setSkillsList(res.data.skills || []);
        } catch (error) {
            toast.error('Failed to load skills');
        }
    };

    const handleSkillToggle = (skill) => {
        const allSkills = [...formData.skills.top_skills];
        if (allSkills.includes(skill)) {
            setFormData({
                ...formData,
                skills: { ...formData.skills, top_skills: allSkills.filter(s => s !== skill) }
            });
        } else if (allSkills.length < 5) {
            setFormData({
                ...formData,
                skills: { ...formData.skills, top_skills: [...allSkills, skill] }
            });
        } else {
            toast.error('You can only select 5 skills');
        }
    };

    const addCustomSkill = () => {
        const trimmed = customSkillInput.trim();
        if (!trimmed) return;
        if (formData.skills.top_skills.length >= 5) {
            toast.error('You can only select 5 skills total');
            return;
        }
        if (formData.skills.top_skills.includes(trimmed)) {
            toast.error('Skill already added');
            return;
        }
        setFormData({
            ...formData,
            skills: {
                ...formData.skills,
                top_skills: [...formData.skills.top_skills, trimmed],
                custom_skills: [...(formData.skills.custom_skills || []), trimmed]
            }
        });
        setCustomSkillInput('');
    };

    const moveSkill = (index, direction) => {
        const skills = [...formData.skills.top_skills];
        const newIndex = index + direction;
        if (newIndex >= 0 && newIndex < skills.length) {
            [skills[index], skills[newIndex]] = [skills[newIndex], skills[index]];
            setFormData({ ...formData, skills: { ...formData.skills, top_skills: skills } });
        }
    };

    // Toggle for multi-select block choices
    const toggleOption = (field, subfield, option) => {
        const current = formData[field]?.[subfield] || [];
        const updated = current.includes(option)
            ? current.filter(o => o !== option)
            : [...current, option];
        setFormData({ ...formData, [field]: { ...formData[field], [subfield]: updated } });
    };

    // Toggle for problem_solving.q1_problem_types (array)
    const toggleProblemType = (option) => {
        const current = formData.problem_solving.q1_problem_types || [];
        const updated = current.includes(option)
            ? current.filter(o => o !== option)
            : [...current, option];
        setFormData({ ...formData, problem_solving: { ...formData.problem_solving, q1_problem_types: updated } });
    };

    const canProceed = () => {
        switch(step) {
            case 1:
                return formData.header.current_role.title.trim().length > 0 &&
                       formData.header.current_role.years.length > 0 &&
                       formData.header.current_role.status.length > 0 &&
                       formData.sector.length > 0 &&
                       formData.header.primary_role_id.length > 0 &&
                       formData.header.location.length > 0 &&
                       formData.header.work_preference.length > 0 &&
                       formData.header.availability_start.trim().length > 0;
            case 2:
                return formData.skills.top_skills.length === 5 &&
                       formData.skills.skill_example_1.trim().length > 20 &&
                       formData.skills.skill_example_2.trim().length > 20 &&
                       formData.skills.skill_improvements.trim().length > 10;
            case 3:
                return formData.problem_solving.q1_problem_types.length > 0 &&
                       formData.problem_solving.q2_figured_out.trim().length > 30 &&
                       formData.problem_solving.q10_risky_decision.trim().length > 30;
            case 4:
                return formData.communication.q1_explain_to_others.trim().length > 30 &&
                       formData.communication.q2_input_vs_independent.trim().length > 20;
            case 5:
                return formData.working_style.q1_environments.length > 0 &&
                       formData.working_style.q2_team.length > 0 &&
                       formData.working_style.q3_manager.length > 0;
            case 6:
                return formData.working_style.q8_deadlines.length > 0 &&
                       formData.working_style.q6_instructions.length > 0 &&
                       formData.working_style.q11_next_role.trim().length > 10;
            case 7:
                return formData.achievements.achievement_1.headline.trim().length > 5 &&
                       formData.achievements.achievement_1.description.trim().length > 30 &&
                       formData.achievements.achievement_1.evidence.trim().length > 5;
            case 8:
                return formData.trial_readiness.trial_length.length > 0 &&
                       formData.trial_readiness.availability.length > 0 &&
                       formData.trial_readiness.work_location.length > 0;
            case 9:
                if (showAntiCheatModal) return antiCheatAcknowledged;
                const allAnswered = microtestQuestions.every(q => 
                    (microtestAnswers[q.id] || '').trim().length >= 50
                );
                return allAnswered || Object.keys(formData.micro_test_response).length > 0;
            default:
                return true;
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setApiComplete(false);
        setGenerating(true);
        try {
            // Format salary expectation for storage
            const submissionData = { ...formData };
            // Backend stores a single combined skill_examples string — combine the
            // two per-skill answer boxes into that shape, no backend schema change needed.
            const skill1 = formData.skills.top_skills[0] || 'Skill 1';
            const skill2 = formData.skills.top_skills[1] || 'Skill 2';
            const { skill_example_1, skill_example_2, ...skillsWithoutRawBoxes } = submissionData.skills;
            submissionData.skills = {
                ...skillsWithoutRawBoxes,
                skill_examples: `${skill1}: ${skill_example_1}\n\n${skill2}: ${skill_example_2}`
            };
            if (submissionData.trial_readiness.pay_expectations) {
                const amount = submissionData.trial_readiness.pay_expectations;
                const format = submissionData.trial_readiness.salary_format;
                submissionData.trial_readiness.pay_expectations = format === 'annual' 
                    ? `£${Number(amount).toLocaleString()}/year`
                    : `£${Number(amount).toLocaleString()}/day`;
            }
            await assessments.submit(submissionData);
            setApiComplete(true);
        } catch (error) {
            console.error('Submit error:', error);
            // Talent Card AI generation can take 60s+, which can outlast a
            // proxy/gateway timeout even though the backend keeps processing
            // and finishes successfully in the background. Before stranding
            // the candidate back on the question form, poll the lightweight
            // status endpoint to check whether it actually completed anyway.
            const completedAnyway = await pollForAssessmentCompletion();
            if (completedAnyway) {
                setApiComplete(true);
            } else {
                toast.error(error.userMessage || 'Failed to submit assessment');
                setGenerating(false);
            }
        } finally {
            setLoading(false);
        }
    };

    // Polls GET /assessments/status every 8s for up to ~4 minutes. Only used
    // as a fallback when the submit request itself errored out client-side
    // (see handleSubmit's catch above) — the happy path never calls this.
    const pollForAssessmentCompletion = async () => {
        for (let attempt = 0; attempt < 30; attempt++) {
            await new Promise(resolve => setTimeout(resolve, 8000));
            try {
                const res = await assessments.getStatus();
                if (res.data?.completed) return true;
            } catch (e) {
                // Transient polling error — keep trying, don't give up early.
            }
        }
        return false;
    };

    const getStepInfo = () => {
        const steps = [
            { icon: User, title: 'Your Profile', subtitle: 'Basic information' },
            { icon: Target, title: 'Skills & Evidence', subtitle: 'Your top 5 skills' },
            { icon: Brain, title: 'How You Think', subtitle: 'Problem solving & risk' },
            { icon: MessageSquare, title: 'Communication', subtitle: 'How you work with others' },
            { icon: Heart, title: 'Working Style', subtitle: 'Environment & team fit' },
            { icon: Clock, title: 'Pace & Structure', subtitle: 'Deadlines & direction' },
            { icon: Trophy, title: 'Achievements', subtitle: 'Proof of work' },
            { icon: Calendar, title: 'Availability', subtitle: 'When you can start' },
            { icon: Sparkles, title: 'Skill Challenge', subtitle: 'Show your skills' }
        ];
        return steps[step - 1] || steps[0];
    };

    // Shared block choice renderer
    const BlockChoice = ({ options, selected, onToggle, color = '#9333EA', multi = true }) => (
        <div className="grid grid-cols-2 gap-2 mt-3">
            {options.map(option => {
                const isSelected = multi ? (selected || []).includes(option) : selected === option;
                return (
                    <button
                        key={option}
                        type="button"
                        onClick={() => onToggle(option)}
                        className={`px-4 py-3 rounded-lg text-sm font-medium text-left transition-all border ${
                            isSelected
                                ? 'text-white border-transparent'
                                : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                        style={isSelected ? { backgroundColor: color } : {}}
                    >
                        {option}
                    </button>
                );
            })}
        </div>
    );

    // Single-select block choice
    const SingleBlockChoice = ({ options, value, onChange, color = '#9333EA' }) => (
        <div className="grid grid-cols-1 gap-2 mt-3">
            {options.map(option => {
                const isSelected = value === option;
                return (
                    <button
                        key={option}
                        type="button"
                        onClick={() => onChange(option)}
                        className={`px-4 py-3 rounded-lg text-sm font-medium text-left transition-all border ${
                            isSelected
                                ? 'text-white border-transparent'
                                : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                        style={isSelected ? { backgroundColor: color } : {}}
                    >
                        {option}
                    </button>
                );
            })}
        </div>
    );

    const renderStep = () => {
        switch(step) {
            // ═══════════════════════════════════════
            // STEP 1: PROFILE
            // ═══════════════════════════════════════
            case 1:
                return (
                    <div className="space-y-6" data-testid="step-header">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>Your Profile</h2>
                            <p className="text-gray-600">Tell employers who you are and what you're looking for</p>
                        </div>
                        
                        {/* Current/most recent role */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="current_role_title">Current or most recent job title *</Label>
                                <Input id="current_role_title" value={formData.header.current_role.title}
                                    onChange={(e) => setFormData({...formData, header: {...formData.header, current_role: {...formData.header.current_role, title: e.target.value}}})}
                                    placeholder="e.g. Account Manager" className="mt-2"
                                    data-testid="current-role-title-input" />
                            </div>
                            <div>
                                <Label htmlFor="current_role_company">Company name</Label>
                                <Input id="current_role_company" value={formData.header.current_role.company}
                                    onChange={(e) => setFormData({...formData, header: {...formData.header, current_role: {...formData.header.current_role, company: e.target.value}}})}
                                    placeholder="e.g. Acme Ltd" className="mt-2"
                                    data-testid="current-role-company-input" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="current_role_years">Years in this role *</Label>
                                <select id="current_role_years" value={formData.header.current_role.years}
                                    onChange={(e) => setFormData({...formData, header: {...formData.header, current_role: {...formData.header.current_role, years: e.target.value}}})}
                                    className="w-full h-11 rounded-md border border-input bg-white px-3 py-2 text-sm mt-2"
                                    data-testid="current-role-years-input">
                                    <option value="">Select years...</option>
                                    <option value="Less than 1 year">Less than 1 year</option>
                                    <option value="1-2 years">1-2 years</option>
                                    <option value="2-5 years">2-5 years</option>
                                    <option value="5-10 years">5-10 years</option>
                                    <option value="10+ years">10+ years</option>
                                </select>
                            </div>
                            <div>
                                <Label htmlFor="current_role_status">Employment status *</Label>
                                <select id="current_role_status" value={formData.header.current_role.status}
                                    onChange={(e) => setFormData({...formData, header: {...formData.header, current_role: {...formData.header.current_role, status: e.target.value}}})}
                                    className="w-full h-11 rounded-md border border-input bg-white px-3 py-2 text-sm mt-2"
                                    data-testid="current-role-status-input">
                                    <option value="">Select status...</option>
                                    <option value="Currently employed">Currently employed</option>
                                    <option value="Recently left">Recently left</option>
                                    <option value="Looking for my first role">Looking for my first role</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="p-4 rounded-lg border-2" style={{ borderColor: '#9333EA', backgroundColor: '#FAF5FF' }}>
                            <Label className="text-lg font-semibold" style={{ color: '#9333EA' }}>Find Your Role *</Label>
                            <p className="text-sm text-gray-600 mb-3">Your microtest will be based on this role.</p>

                            {!browseBySector ? (
                                <div>
                                    <Input
                                        type="text"
                                        value={roleSearchQuery}
                                        onChange={(e) => setRoleSearchQuery(e.target.value)}
                                        placeholder="Search for your role e.g. Account Manager, Graphic Designer..."
                                        data-testid="role-search-input"
                                    />
                                    {roleSearchQuery.trim().length > 0 && (
                                        <div className="mt-2 max-h-72 overflow-y-auto border border-purple-200 rounded-md bg-white divide-y" data-testid="role-search-results">
                                            {filteredRoles.length === 0 ? (
                                                <div className="p-3 text-sm text-slate-500" data-testid="role-search-empty">No matching roles.</div>
                                            ) : (
                                                filteredRoles.map(r => (
                                                    <button key={`${r.sector_id}::${r.id}`} type="button"
                                                        onClick={() => selectRoleAndSector(r)}
                                                        className="w-full text-left p-3 hover:bg-purple-50 transition-colors"
                                                        data-testid={`role-search-result-${r.sector_id}-${r.id}`}>
                                                        <div className="font-bold text-gray-900">{r.name}</div>
                                                        <div className="text-sm text-slate-500">{r.sector_name}</div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    {!browseSelectedSector ? (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="browse-sector-grid">
                                            {sectors.map(s => (
                                                <button key={s.id} type="button"
                                                    onClick={() => setBrowseSelectedSector(s)}
                                                    className="p-3 rounded-md border border-purple-200 bg-white hover:border-purple-400 hover:shadow-sm text-left transition-all"
                                                    data-testid={`browse-sector-${s.id}`}>
                                                    <div className="font-bold text-gray-900">{s.name}</div>
                                                    {s.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description}</div>}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div>
                                            <button type="button" onClick={() => setBrowseSelectedSector(null)}
                                                className="text-sm text-purple-600 hover:underline mb-2 inline-flex items-center"
                                                data-testid="browse-back-to-sectors">
                                                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to sectors
                                            </button>
                                            <div className="font-bold text-gray-900 mb-2">{browseSelectedSector.name}</div>
                                            <div className="max-h-72 overflow-y-auto border border-purple-200 rounded-md bg-white divide-y" data-testid={`browse-role-list-${browseSelectedSector.id}`}>
                                                {allRoles.filter(r => r.sector_id === browseSelectedSector.id).map(r => (
                                                    <button key={r.id} type="button" onClick={() => selectRoleAndSector(r)}
                                                        className="w-full text-left p-3 hover:bg-purple-50 transition-colors"
                                                        data-testid={`browse-role-${r.id}`}>
                                                        <div className="font-bold text-gray-900">{r.name}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mt-3">
                                <button type="button"
                                    onClick={() => { setBrowseBySector(!browseBySector); setBrowseSelectedSector(null); setRoleSearchQuery(''); }}
                                    className="text-sm font-medium text-purple-700 hover:underline"
                                    data-testid="toggle-browse-mode">
                                    {browseBySector ? '← Search by role name' : 'Browse by sector →'}
                                </button>
                            </div>

                            <p className="text-xs text-slate-500 mt-3" data-testid="role-disclaimer">
                                <span className="font-bold text-slate-700">Can&rsquo;t find your exact role?</span> Select the closest match for now &mdash;{' '}
                                <a href="mailto:hello@sampler.org.uk?subject=Role%20request" className="font-semibold text-purple-700 hover:underline" data-testid="request-role-link">
                                    request it
                                </a>{' '}
                                and we&rsquo;ll add it soon.
                            </p>

                            {formData.header.primary_role_id && (
                                <div className="mt-3 p-3 rounded-md bg-purple-100 border border-purple-300" data-testid="selected-role-pill">
                                    <div className="text-xs text-purple-700 mb-0.5 uppercase tracking-wide">Selected role</div>
                                    <div className="font-bold text-purple-900">{formData.header.primary_role}</div>
                                    <div className="text-sm text-purple-700">{sectors.find(s => s.id === formData.sector)?.name}</div>
                                </div>
                            )}
                        </div>
                        
                        {formData.header.primary_role_id && (
                            <div>
                                <Label htmlFor="secondary_role">Secondary Role (Optional)</Label>
                                <select id="secondary_role" value={formData.header.secondary_role_id}
                                    onChange={(e) => {
                                        const selectedRole = availableRoles.find(r => r.id === e.target.value);
                                        setFormData({...formData, header: {...formData.header, secondary_role_id: e.target.value, secondary_role: selectedRole?.name || ''}});
                                    }}
                                    className="w-full h-11 rounded-md border border-input bg-white px-3 py-2 text-sm mt-2"
                                    disabled={loadingRoles}
                                    data-testid="secondary-role-select">
                                    <option value="">None (optional)</option>
                                    {availableRoles.filter(role => role.id !== formData.header.primary_role_id).map(role => (
                                        <option key={role.id} value={role.id}>{role.name}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Optionally pick a second role within the same sector</p>
                            </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <Label htmlFor="location">Location *</Label>
                                <select id="location" value={formData.header.location}
                                    onChange={(e) => setFormData({...formData, header: {...formData.header, location: e.target.value}})}
                                    className="w-full h-11 rounded-md border border-input bg-white px-3 py-2 text-sm mt-2">
                                    <option value="">Select location...</option>
                                    <option value="London, United Kingdom">London, United Kingdom</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1 italic">More locations incoming</p>
                            </div>
                            <div>
                                <Label htmlFor="work_preference">Work Preference *</Label>
                                <select id="work_preference" value={formData.header.work_preference}
                                    onChange={(e) => setFormData({...formData, header: {...formData.header, work_preference: e.target.value}})}
                                    className="w-full h-11 rounded-md border border-input bg-white px-3 py-2 text-sm mt-2">
                                    <option value="">Select preference...</option>
                                    <option value="Remote">Remote</option>
                                    <option value="Hybrid">Hybrid</option>
                                    <option value="On-site">On-site</option>
                                    <option value="Flexible">Flexible</option>
                                </select>
                            </div>
                        </div>
                        
                        <div>
                            <Label htmlFor="availability_start">When can you start? *</Label>
                            <Input id="availability_start" value={formData.header.availability_start}
                                onChange={(e) => setFormData({...formData, header: {...formData.header, availability_start: e.target.value}})}
                                placeholder="e.g., Immediately, 2 weeks, January 2026" className="mt-2" />
                        </div>
                    </div>
                );
            
            // ═══════════════════════════════════════
            // STEP 2: SKILLS & EVIDENCE
            // ═══════════════════════════════════════
            case 2:
                return (
                    <div className="space-y-6" data-testid="step-skills">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>Skills & Evidence</h2>
                            <p className="text-gray-600">Choose your top 5 skills, rank them, and give real examples ({formData.skills.top_skills.length}/5)</p>
                        </div>
                        
                        {/* Skill grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {skillsList.map(skill => (
                                <button key={skill} onClick={() => handleSkillToggle(skill)}
                                    className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all border ${
                                        formData.skills.top_skills.includes(skill) ? 'text-white border-transparent' : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
                                    }`}
                                    style={formData.skills.top_skills.includes(skill) ? { backgroundColor: '#9333EA' } : {}}
                                    data-testid={`skill-${skill.toLowerCase().replace(/\s+/g, '-')}`}>
                                    {skill}
                                </button>
                            ))}
                        </div>
                        
                        {/* Custom skill input */}
                        <div className="flex gap-2">
                            <Input value={customSkillInput} onChange={(e) => setCustomSkillInput(e.target.value)}
                                placeholder="Type a skill not listed above..." className="flex-1"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomSkill(); } }}
                                data-testid="custom-skill-input" />
                            <Button type="button" variant="outline" onClick={addCustomSkill}
                                className="border-purple-200 text-purple-600 hover:bg-purple-50"
                                data-testid="add-custom-skill">Add</Button>
                        </div>

                        {/* Ranked skills with reorder */}
                        {formData.skills.top_skills.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-gray-700">Your skills (tap the arrows to reorder, strongest first):</p>
                                {formData.skills.top_skills.map((skill, idx) => (
                                    <div key={skill} className="flex items-center space-x-3 p-3 rounded-lg" style={{ backgroundColor: '#F3E8FF' }}>
                                        <span className="text-xl font-bold w-7" style={{ color: '#9333EA' }}>{idx + 1}</span>
                                        <span className="flex-1 font-semibold text-gray-800">{skill}</span>
                                        <div className="flex space-x-1">
                                            <Button size="sm" variant="outline" onClick={() => moveSkill(idx, -1)} disabled={idx === 0} className="h-8 w-8 p-0">
                                                <ChevronUp className="h-4 w-4" />
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => moveSkill(idx, 1)} disabled={idx === formData.skills.top_skills.length - 1} className="h-8 w-8 p-0">
                                                <ChevronDown className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* Examples - two separate boxes, one per skill, so both must be answered */}
                        <div>
                            <Label className="text-base font-semibold">
                                Give a real example of when you used{' '}
                                <span style={{ color: '#9333EA' }}>{formData.skills.top_skills[0] || 'your top skill'}</span> well.
                            </Label>
                            <Textarea value={formData.skills.skill_example_1}
                                onChange={(e) => setFormData({...formData, skills: {...formData.skills, skill_example_1: e.target.value}})}
                                placeholder="Describe a specific situation, what you did, and the result."
                                rows={4} className="mt-2" data-testid="skill-example-1" />
                        </div>

                        <div>
                            <Label className="text-base font-semibold">
                                Give a real example of when you used{' '}
                                <span style={{ color: '#9333EA' }}>{formData.skills.top_skills[1] || 'your second skill'}</span> well.
                            </Label>
                            <Textarea value={formData.skills.skill_example_2}
                                onChange={(e) => setFormData({...formData, skills: {...formData.skills, skill_example_2: e.target.value}})}
                                placeholder="Describe a specific situation, what you did, and the result."
                                rows={4} className="mt-2" data-testid="skill-example-2" />
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">Where do you want to improve?</Label>
                            <Textarea value={formData.skills.skill_improvements}
                                onChange={(e) => setFormData({...formData, skills: {...formData.skills, skill_improvements: e.target.value}})}
                                placeholder={"e.g., I want to get better at presenting to senior stakeholders and staying calm when multiple urgent issues hit at once."}
                                rows={3} className="mt-2" data-testid="skill-improvements" />
                        </div>
                    </div>
                );
            
            // ═══════════════════════════════════════
            // STEP 3: HOW YOU THINK
            // ═══════════════════════════════════════
            case 3:
                return (
                    <div className="space-y-6" data-testid="step-problem-solving">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>How You Think</h2>
                            <p className="text-gray-600">Problem solving, initiative, and how you handle risk</p>
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">What kinds of problems do you enjoy solving most? <span className="text-gray-500 font-normal">(Select all that apply)</span></Label>
                            <BlockChoice
                                options={PROBLEM_TYPE_OPTIONS}
                                selected={formData.problem_solving.q1_problem_types}
                                onToggle={toggleProblemType}
                                color="#9333EA"
                            />
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">
                                Tell us about a time you figured something out without being shown or asked.
                            </Label>
                            <p className="text-sm text-gray-500 mb-2">What triggered you to act, and how did you approach it?</p>
                            <Textarea value={formData.problem_solving.q2_figured_out}
                                onChange={(e) => setFormData({...formData, problem_solving: {...formData.problem_solving, q2_figured_out: e.target.value}})}
                                placeholder="Walk us through the situation and your approach..."
                                rows={4} className="mt-1" data-testid="q2-figured-out" />
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">
                                Tell us about a decision that felt risky at the time but made sense to you.
                            </Label>
                            <p className="text-sm text-gray-500 mb-2">What factors mattered most in your thinking?</p>
                            <Textarea value={formData.problem_solving.q10_risky_decision}
                                onChange={(e) => setFormData({...formData, problem_solving: {...formData.problem_solving, q10_risky_decision: e.target.value}})}
                                placeholder="Describe the decision and your reasoning..."
                                rows={4} className="mt-1" data-testid="q10-risky" />
                        </div>
                    </div>
                );
            
            // ═══════════════════════════════════════
            // STEP 4: COMMUNICATION
            // ═══════════════════════════════════════
            case 4:
                return (
                    <div className="space-y-6" data-testid="step-communication">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>Communication</h2>
                            <p className="text-gray-600">How you explain, simplify, and make decisions with others</p>
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">
                                How do you explain things to people who don't share your context?
                            </Label>
                            <p className="text-sm text-gray-500 mb-2">Think of a time you had to simplify something complicated — what did you keep, and what did you cut?</p>
                            <Textarea value={formData.communication.q1_explain_to_others}
                                onChange={(e) => setFormData({...formData, communication: {...formData.communication, q1_explain_to_others: e.target.value}})}
                                placeholder="Walk us through your approach to simplifying something for someone else..."
                                rows={4} className="mt-1" data-testid="comm-q1" />
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">
                                When do you prefer to ask for input, and when do you decide independently?
                            </Label>
                            <p className="text-sm text-gray-500 mb-2">Give a brief example of each.</p>
                            <Textarea value={formData.communication.q2_input_vs_independent}
                                onChange={(e) => setFormData({...formData, communication: {...formData.communication, q2_input_vs_independent: e.target.value}})}
                                placeholder="Describe both scenarios with examples..."
                                rows={4} className="mt-1" data-testid="comm-q2" />
                        </div>
                    </div>
                );
            
            // ═══════════════════════════════════════
            // STEP 5: WORKING STYLE
            // ═══════════════════════════════════════
            case 5:
                return (
                    <div className="space-y-6" data-testid="step-working-style">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>Working Style</h2>
                            <p className="text-gray-600">Help us understand where you do your best work</p>
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">
                                Which environments motivate you most? <span className="text-gray-500 font-normal">(Select all that apply)</span>
                            </Label>
                            <BlockChoice options={ENVIRONMENT_OPTIONS} selected={formData.working_style.q1_environments}
                                onToggle={(opt) => toggleOption('working_style', 'q1_environments', opt)} color="#9333EA" />
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">
                                What kind of team do you do your best work in? <span className="text-gray-500 font-normal">(Select all that apply)</span>
                            </Label>
                            <BlockChoice options={TEAM_OPTIONS} selected={formData.working_style.q2_team}
                                onToggle={(opt) => toggleOption('working_style', 'q2_team', opt)} color="#14B8A6" />
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">
                                What kind of manager helps you do your best work? <span className="text-gray-500 font-normal">(Select all that apply)</span>
                            </Label>
                            <BlockChoice options={MANAGER_OPTIONS} selected={formData.working_style.q3_manager}
                                onToggle={(opt) => toggleOption('working_style', 'q3_manager', opt)} color="#9333EA" />
                        </div>
                    </div>
                );
            
            // ═══════════════════════════════════════
            // STEP 6: PACE & STRUCTURE
            // ═══════════════════════════════════════
            case 6:
                return (
                    <div className="space-y-6" data-testid="step-pace">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>Pace & Structure</h2>
                            <p className="text-gray-600">How you handle deadlines, direction, and what you're looking for</p>
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">How do you feel about deadlines?</Label>
                            <SingleBlockChoice options={DEADLINE_OPTIONS} value={formData.working_style.q8_deadlines}
                                onChange={(opt) => setFormData({...formData, working_style: {...formData.working_style, q8_deadlines: opt}})}
                                color="#9333EA" />
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">Do you do your best work with clear instructions or loose direction?</Label>
                            <SingleBlockChoice options={INSTRUCTION_OPTIONS} value={formData.working_style.q6_instructions}
                                onChange={(opt) => setFormData({...formData, working_style: {...formData.working_style, q6_instructions: opt}})}
                                color="#14B8A6" />
                        </div>
                        
                        <div>
                            <Label className="text-base font-semibold">What are you looking for in your next role?</Label>
                            <p className="text-sm text-gray-500 mb-2">Focus on environment, scope, and growth rather than title.</p>
                            <Textarea value={formData.working_style.q11_next_role}
                                onChange={(e) => setFormData({...formData, working_style: {...formData.working_style, q11_next_role: e.target.value}})}
                                placeholder="What matters most to you in your next role?"
                                rows={3} className="mt-1" data-testid="next-role" />
                        </div>
                    </div>
                );
            
            // ═══════════════════════════════════════
            // STEP 7: ACHIEVEMENTS
            // ═══════════════════════════════════════
            case 7:
                return (
                    <div className="space-y-6" data-testid="step-achievements">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>Achievements</h2>
                            <p className="text-gray-600">Show real outcomes you helped create (1 required, 2 optional)</p>
                        </div>
                        
                        <div className="p-4 rounded-lg border-l-4" style={{ backgroundColor: '#F0FDF4', borderColor: '#14B8A6' }}>
                            <p className="text-sm text-gray-600">
                                Think of moments where something was meaningfully better because you were involved. What was the situation? What did you do? What changed?
                            </p>
                        </div>
                        
                        {/* Achievement 1 - Required */}
                        <div className="p-5 rounded-xl border-2" style={{ borderColor: '#9333EA', backgroundColor: '#FAFAFF' }}>
                            <h3 className="font-bold text-lg mb-4" style={{ color: '#9333EA' }}>Achievement 1 <span className="text-gray-500 font-normal text-sm">(Required)</span></h3>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-sm font-semibold">Headline — What did you improve, fix, or help create?</Label>
                                    <Input value={formData.achievements.achievement_1.headline}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_1: {...formData.achievements.achievement_1, headline: e.target.value}}})}
                                        placeholder="e.g., Reduced customer wait times by redesigning the queue system" className="mt-1" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold">What happened? (Context, your actions, the outcome)</Label>
                                    <Textarea value={formData.achievements.achievement_1.description}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_1: {...formData.achievements.achievement_1, description: e.target.value}}})}
                                        placeholder="Describe the problem, what you personally did, and what changed as a result..."
                                        rows={4} className="mt-1" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold">Evidence — What can you point to that proves this? (Numbers, feedback, things that still exist)</Label>
                                    <Textarea value={formData.achievements.achievement_1.evidence}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_1: {...formData.achievements.achievement_1, evidence: e.target.value}}})}
                                        placeholder="e.g., Saved the team 10 hours/week. The process I built is still used today. Manager referenced it in my review."
                                        rows={2} className="mt-1" />
                                </div>
                            </div>
                        </div>
                        
                        {/* Achievement 2 - Optional */}
                        <div className="p-5 rounded-xl border border-gray-200 bg-white">
                            <h3 className="font-bold text-lg mb-4 text-gray-600">Achievement 2 <span className="text-gray-400 font-normal text-sm">(Optional)</span></h3>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-sm font-semibold text-gray-600">Headline</Label>
                                    <Input value={formData.achievements.achievement_2.headline}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_2: {...formData.achievements.achievement_2, headline: e.target.value}}})}
                                        placeholder="What did you improve, fix, or help create?" className="mt-1" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold text-gray-600">What happened?</Label>
                                    <Textarea value={formData.achievements.achievement_2.description}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_2: {...formData.achievements.achievement_2, description: e.target.value}}})}
                                        placeholder="Context, your actions, the outcome..." rows={3} className="mt-1" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold text-gray-600">Evidence</Label>
                                    <Input value={formData.achievements.achievement_2.evidence}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_2: {...formData.achievements.achievement_2, evidence: e.target.value}}})}
                                        placeholder="Numbers, feedback, things that still exist..." className="mt-1" />
                                </div>
                            </div>
                        </div>
                        
                        {/* Achievement 3 - Optional */}
                        <div className="p-5 rounded-xl border border-gray-200 bg-white">
                            <h3 className="font-bold text-lg mb-4 text-gray-600">Achievement 3 <span className="text-gray-400 font-normal text-sm">(Optional)</span></h3>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-sm font-semibold text-gray-600">Headline</Label>
                                    <Input value={formData.achievements.achievement_3.headline}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_3: {...formData.achievements.achievement_3, headline: e.target.value}}})}
                                        placeholder="What did you improve, fix, or help create?" className="mt-1" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold text-gray-600">What happened?</Label>
                                    <Textarea value={formData.achievements.achievement_3.description}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_3: {...formData.achievements.achievement_3, description: e.target.value}}})}
                                        placeholder="Context, your actions, the outcome..." rows={3} className="mt-1" />
                                </div>
                                <div>
                                    <Label className="text-sm font-semibold text-gray-600">Evidence</Label>
                                    <Input value={formData.achievements.achievement_3.evidence}
                                        onChange={(e) => setFormData({...formData, achievements: {...formData.achievements, achievement_3: {...formData.achievements.achievement_3, evidence: e.target.value}}})}
                                        placeholder="Numbers, feedback, things that still exist..." className="mt-1" />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            
            // ═══════════════════════════════════════
            // STEP 8: TRIAL READINESS
            // ═══════════════════════════════════════
            case 8:
                return (
                    <div className="space-y-6" data-testid="step-trial-readiness">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>Availability</h2>
                            <p className="text-gray-600">Help employers understand when and how you can participate</p>
                        </div>
                        
                        <div className="p-4 rounded-lg border border-teal-200" style={{ backgroundColor: '#F0FDFA' }}>
                            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                                <Calendar className="h-5 w-5 mr-2" style={{ color: '#14B8A6' }} /> Availability
                            </h3>
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <Label htmlFor="trial_length">Duration you can commit to *</Label>
                                    <select id="trial_length" value={formData.trial_readiness.trial_length}
                                        onChange={(e) => setFormData({...formData, trial_readiness: {...formData.trial_readiness, trial_length: e.target.value}})}
                                        className="w-full h-11 rounded-md border border-input bg-white px-3 py-2 text-sm mt-2">
                                        <option value="">Select...</option>
                                        <option value="1 day">1 day</option>
                                        <option value="2-3 days">2-3 days</option>
                                        <option value="1 week">1 week (5 days)</option>
                                        <option value="Up to 10 days">Up to 10 days</option>
                                        <option value="Flexible">Flexible (any duration)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mt-4">
                                <Label htmlFor="availability">Availability *</Label>
                                <div className="grid grid-cols-1 gap-2 mt-2" data-testid="availability-options">
                                    {[
                                        'Available now',
                                        'Available in the next week',
                                        'Available in the next 2 weeks',
                                        'Available in the next month',
                                        'Custom'
                                    ].map(opt => (
                                        <button
                                            key={opt}
                                            type="button"
                                            onClick={() => setFormData({...formData, trial_readiness: {...formData.trial_readiness, availability: opt}})}
                                            className={`px-4 py-3 rounded-xl text-sm font-medium text-left transition-all duration-200 border ${
                                                formData.trial_readiness.availability === opt
                                                    ? 'border-[#9333EA] bg-[#F3E8FF] text-[#9333EA] font-semibold'
                                                    : 'border-gray-200 text-gray-600 hover:border-[#9333EA] hover:bg-purple-50'
                                            }`}
                                            data-testid={`availability-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-4 rounded-lg border border-purple-200" style={{ backgroundColor: '#FAF5FF' }}>
                            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                                <MapPin className="h-5 w-5 mr-2" style={{ color: '#9333EA' }} /> Location & Salary Expectation
                            </h3>
                            <div className="mb-4">
                                <Label htmlFor="work_location">Work arrangement preference *</Label>
                                <select id="work_location" value={formData.trial_readiness.work_location}
                                    onChange={(e) => setFormData({...formData, trial_readiness: {...formData.trial_readiness, work_location: e.target.value}})}
                                    className="w-full h-11 rounded-md border border-input bg-white px-3 py-2 text-sm mt-2">
                                    <option value="">Select...</option>
                                    <option value="On-site only">On-site only</option>
                                    <option value="Remote only">Remote only</option>
                                    <option value="Hybrid preferred">Hybrid preferred</option>
                                    <option value="Flexible">Flexible (any arrangement)</option>
                                </select>
                            </div>
                            <div>
                                <Label>Salary Expectation</Label>
                                <div className="flex items-center gap-2 mt-2 mb-3">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({...formData, trial_readiness: {...formData.trial_readiness, salary_format: 'annual'}})}
                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                            formData.trial_readiness.salary_format === 'annual'
                                                ? 'bg-[#9333EA] text-white'
                                                : 'border border-gray-200 text-gray-600 hover:border-[#9333EA]'
                                        }`}
                                        data-testid="salary-toggle-annual"
                                    >
                                        Annual Salary
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({...formData, trial_readiness: {...formData.trial_readiness, salary_format: 'day_rate'}})}
                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                            formData.trial_readiness.salary_format === 'day_rate'
                                                ? 'bg-[#9333EA] text-white'
                                                : 'border border-gray-200 text-gray-600 hover:border-[#9333EA]'
                                        }`}
                                        data-testid="salary-toggle-day-rate"
                                    >
                                        Day Rate
                                    </button>
                                </div>
                                <Input id="pay_expectations" value={formData.trial_readiness.pay_expectations}
                                    onChange={(e) => setFormData({...formData, trial_readiness: {...formData.trial_readiness, pay_expectations: e.target.value}})}
                                    placeholder={formData.trial_readiness.salary_format === 'annual' ? 'e.g., 35000' : 'e.g., 200'}
                                    className="mt-1" type="number" />
                                <p className="text-xs text-gray-500 mt-1">
                                    {formData.trial_readiness.salary_format === 'annual'
                                        ? 'Enter your expected annual salary in GBP (numbers only)'
                                        : 'Enter your expected day rate in GBP (numbers only)'
                                    }
                                </p>
                            </div>
                        </div>
                        
                        <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                                <MessageSquare className="h-5 w-5 mr-2 text-gray-600" /> Flexibility & Constraints
                            </h3>
                            <div>
                                <Label htmlFor="constraints">Anything we'd need to accommodate for you to participate?</Label>
                                <Textarea id="constraints" value={formData.trial_readiness.constraints}
                                    onChange={(e) => setFormData({...formData, trial_readiness: {...formData.trial_readiness, constraints: e.target.value}})}
                                    placeholder="e.g., Need to leave by 4pm for school pickup, require wheelchair access, etc."
                                    rows={2} className="mt-2" />
                                <p className="text-xs text-gray-500 mt-1">Optional — focus on operational needs</p>
                            </div>
                        </div>
                    </div>
                );
            
            // ═══════════════════════════════════════
            // STEP 9: MICROTEST
            // ═══════════════════════════════════════
            case 9:
                // Loading state while session initialises
                if (microtestLoading) {
                    return (
                        <div className="space-y-6 text-center py-16" data-testid="step-micro-test-loading">
                            <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#9333EA] border-t-transparent mx-auto" />
                            <h2 className="text-2xl font-bold" style={{ color: '#14B8A6' }}>Preparing Your Skill Challenge</h2>
                            <p className="text-gray-500">Loading microtest questions for your role...</p>
                        </div>
                    );
                }
                
                // Error state with retry
                if (microtestError) {
                    return (
                        <div className="space-y-6 text-center py-16" data-testid="step-micro-test-error">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF2F2' }}>
                                <AlertTriangle className="h-8 w-8 text-red-500" />
                            </div>
                            <h2 className="text-2xl font-bold" style={{ color: '#14B8A6' }}>Couldn't Load Skill Challenge</h2>
                            <p className="text-gray-500 max-w-md mx-auto">{microtestError}</p>
                            <div className="flex justify-center gap-3 mt-4">
                                <Button variant="outline" onClick={() => setStep(8)} data-testid="back-to-step-8">
                                    <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
                                </Button>
                                <Button onClick={() => initializeMicrotestSession(formData.sector, formData.header.primary_role_id, token)}
                                    className="btn-primary" data-testid="retry-microtest">
                                    Try Again
                                </Button>
                            </div>
                        </div>
                    );
                }
                
                // Waiting state — session hasn't been requested yet
                if (!microtestSession && !showAntiCheatModal) {
                    // Auto-trigger if we somehow got here without triggering
                    if (!microtestLoading && !microtestError) {
                        setTimeout(() => initializeMicrotestSession(formData.sector, formData.header.primary_role_id, token), 100);
                    }
                    return (
                        <div className="space-y-6 text-center py-16" data-testid="step-micro-test-loading">
                            <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#9333EA] border-t-transparent mx-auto" />
                            <h2 className="text-2xl font-bold" style={{ color: '#14B8A6' }}>Preparing Your Skill Challenge</h2>
                            <p className="text-gray-500">Loading microtest questions for your role...</p>
                        </div>
                    );
                }
                
                if (showAntiCheatModal) {
                    return (
                        <div className="space-y-6" data-testid="step-micro-test-modal">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
                                    <Shield className="h-8 w-8" style={{ color: '#D97706' }} />
                                </div>
                                <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>Microtest Rules</h2>
                                <p className="text-gray-600">Please read carefully before starting</p>
                            </div>
                            
                            <div className="p-6 rounded-xl border-2 space-y-4" style={{ backgroundColor: '#FFF7ED', borderColor: '#D97706' }}>
                                <div className="flex items-start space-x-3">
                                    <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#D97706' }} />
                                    <div>
                                        <p className="font-semibold text-gray-800">This is a locked assessment section</p>
                                        <p className="text-sm text-gray-600 mt-1">You must complete all questions in ONE continuous session.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#D97706' }} />
                                    <div>
                                        <p className="font-semibold text-gray-800">Tab switching will restart the test</p>
                                        <p className="text-sm text-gray-600 mt-1">Switching tabs, apps, or leaving this page will restart the Microtest. You get ONE warning, then restart.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#D97706' }} />
                                    <div>
                                        <p className="font-semibold text-gray-800">New questions on restart</p>
                                        <p className="text-sm text-gray-600 mt-1">If restarted, previous answers are discarded and new questions may be assigned.</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                                <p className="text-sm text-gray-700">
                                    <strong>Why these rules?</strong> Microtests measure your decision-making under realistic conditions. This ensures fairness for all candidates.
                                </p>
                            </div>
                            
                            <div className="flex items-center space-x-3 p-4 border rounded-lg">
                                <input type="checkbox" id="acknowledge-rules" checked={antiCheatAcknowledged}
                                    onChange={(e) => setAntiCheatAcknowledged(e.target.checked)}
                                    className="h-5 w-5 rounded" style={{ accentColor: '#9333EA' }} />
                                <label htmlFor="acknowledge-rules" className="text-gray-700 font-medium cursor-pointer">
                                    I understand and am ready to begin
                                </label>
                            </div>
                            
                            <Button onClick={handleAcknowledgeAntiCheat} disabled={!antiCheatAcknowledged}
                                className="w-full h-12 text-lg font-semibold"
                                style={{ background: antiCheatAcknowledged ? 'linear-gradient(135deg, #9333EA, #7C3AED)' : '#D1D5DB', color: 'white' }}>
                                Start Microtest ({microtestQuestions.length} Questions)
                            </Button>
                        </div>
                    );
                }
                
                const currentQuestion = microtestQuestions[currentMicrotestIndex];
                const isLastQuestion = currentMicrotestIndex === microtestQuestions.length - 1;
                const currentAnswer = microtestAnswers[currentQuestion?.id] || '';
                
                return (
                    <div className="space-y-6" data-testid="step-micro-test">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2" style={{ color: '#14B8A6' }}>
                                {currentQuestion?.title || 'Microtest Question'}
                            </h2>
                            <p className="text-gray-600">
                                Question {currentMicrotestIndex + 1} of {microtestQuestions.length}
                                {currentMicrotestIndex < 2 && <span className="ml-2 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#F3E8FF', color: '#9333EA' }}>General</span>}
                                {currentMicrotestIndex >= 2 && <span className="ml-2 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#DBEAFE', color: '#2563EB' }}>Role-Specific</span>}
                            </p>
                        </div>
                        
                        {graceUsed && (
                            <div className="p-3 rounded-lg flex items-center space-x-2" style={{ backgroundColor: '#FEF2F2' }}>
                                <AlertTriangle className="h-5 w-5" style={{ color: '#EF4444' }} />
                                <span className="text-sm font-medium" style={{ color: '#EF4444' }}>Warning: Another tab switch will restart the test</span>
                            </div>
                        )}
                        
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="h-2 rounded-full transition-all duration-300"
                                style={{ width: `${((currentMicrotestIndex + 1) / microtestQuestions.length) * 100}%`, backgroundColor: '#9333EA' }} />
                        </div>
                        
                        <div className="p-6 rounded-xl border-2" style={{ backgroundColor: '#F3E8FF', borderColor: '#9333EA' }}>
                            {currentQuestion?.scenario && (
                                <>
                                    <h3 className="font-bold text-lg mb-3" style={{ color: '#9333EA' }}>Scenario</h3>
                                    <p className="text-gray-700 mb-4 whitespace-pre-line">{currentQuestion.scenario}</p>
                                </>
                            )}
                            <h3 className="font-bold text-lg mb-3" style={{ color: '#9333EA' }}>Your Task</h3>
                            <p className="text-gray-700">{currentQuestion?.question || 'Loading...'}</p>
                        </div>
                        
                        <div>
                            <Label htmlFor="microtest_answer" className="text-base font-semibold">Your Response</Label>
                            <Textarea id="microtest_answer" value={currentAnswer}
                                onChange={(e) => setMicrotestAnswers(prev => ({ ...prev, [currentQuestion?.id]: e.target.value }))}
                                placeholder="Take your time. Quality over speed. Show us how you think..."
                                rows={8} className="mt-2" />
                            <p className="text-xs text-gray-500 mt-2">Minimum 50 characters. There's no right answer — we want to see your approach.</p>
                        </div>
                        
                        <div className="flex justify-between">
                            <Button variant="outline"
                                onClick={() => { if (currentMicrotestIndex > 0) setCurrentMicrotestIndex(prev => prev - 1); }}
                                disabled={currentMicrotestIndex === 0}>
                                <ArrowLeft className="mr-2 h-4 w-4" /> Previous
                            </Button>
                            <Button onClick={async () => {
                                    await submitMicrotestAnswer(currentQuestion.id, currentAnswer);
                                    if (isLastQuestion) {
                                        await completeMicrotestSession();
                                        const structuredResponses = {};
                                        microtestQuestions.forEach((q) => {
                                            structuredResponses[q.id] = microtestAnswers[q.id] || currentAnswer;
                                        });
                                        setFormData(prev => ({ ...prev, micro_test_response: structuredResponses }));
                                    } else {
                                        setCurrentMicrotestIndex(prev => prev + 1);
                                    }
                                }}
                                disabled={currentAnswer.trim().length < 50}
                                style={{ background: currentAnswer.trim().length >= 50 ? 'linear-gradient(135deg, #9333EA, #7C3AED)' : '#D1D5DB', color: 'white' }}>
                                {isLastQuestion ? 'Complete Microtest' : 'Next Question'} <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                );
            
            default:
                return null;
        }
    };

    const stepInfo = getStepInfo();
    const StepIcon = stepInfo.icon;

    if (generating) {
        return (
            <GeneratingTalentCardScreen
                apiComplete={apiComplete}
                onContinue={() => navigate('/candidate/dashboard', { state: { showTalentCard: true } })}
            />
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <header className="border-b border-gray-200 bg-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>Sampler</h1>
                    </div>
                    <Button variant="ghost" onClick={() => navigate('/candidate/dashboard')} data-testid="exit-assessment">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Exit
                    </Button>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#9333EA' }}>
                                <StepIcon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-800">{stepInfo.title}</h3>
                                <p className="text-sm text-gray-500">{stepInfo.subtitle}</p>
                            </div>
                        </div>
                        <span className="text-sm font-semibold" style={{ color: '#14B8A6' }}>
                            Step {step} of {TOTAL_STEPS}
                        </span>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all duration-500"
                            style={{ width: `${(step / TOTAL_STEPS) * 100}%`, backgroundColor: '#9333EA' }} />
                    </div>
                    
                    <div className="flex justify-between mt-2">
                        {[...Array(TOTAL_STEPS)].map((_, idx) => (
                            <div key={idx}
                                className={`w-2.5 h-2.5 rounded-full ${idx + 1 <= step ? '' : 'bg-gray-300'}`}
                                style={idx + 1 <= step ? { backgroundColor: '#9333EA' } : {}} />
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
                    {renderStep()}
                    
                    {step !== 9 && (
                        <div className="flex items-center justify-between mt-8 pt-6 border-t">
                            <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="prev-step">
                                <ArrowLeft className="mr-2 h-4 w-4" /> Previous
                            </Button>
                            
                            {step < TOTAL_STEPS ? (
                                <Button onClick={() => {
                                    const nextStep = step + 1;
                                    if (step === 1) {
                                        // Onboarding funnel tracking (admin-only) — fire-and-forget.
                                        // Both fire at this same transition: details_completed
                                        // (the details form validated + Next clicked) and
                                        // assessment_started (the substantive assessment begins).
                                        onboarding.track('details_completed');
                                        onboarding.track('assessment_started');
                                    }
                                    setStep(nextStep);
                                    if (nextStep === 9) {
                                        initializeMicrotestSession(formData.sector, formData.header.primary_role_id, token);
                                    }
                                }} disabled={!canProceed()} className="btn-primary" data-testid="next-step">
                                    Next <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            ) : (
                                <Button onClick={handleSubmit} disabled={!canProceed() || loading} className="btn-primary" data-testid="submit-assessment">
                                    {loading ? (
                                        <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" /> Generating Talent Card...</>
                                    ) : (
                                        <><Sparkles className="mr-2 h-4 w-4" /> Generate My Talent Card</>
                                    )}
                                </Button>
                            )}
                        </div>
                    )}
                    
                    {step === 9 && !showAntiCheatModal && Object.keys(formData.micro_test_response).length > 0 && (
                        <div className="flex items-center justify-between mt-8 pt-6 border-t">
                            <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="prev-step">
                                <ArrowLeft className="mr-2 h-4 w-4" /> Previous
                            </Button>
                            <Button onClick={handleSubmit} disabled={!canProceed() || loading} className="btn-primary" data-testid="submit-assessment">
                                {loading ? (
                                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" /> Generating Talent Card...</>
                                ) : (
                                    <><Sparkles className="mr-2 h-4 w-4" /> Generate My Talent Card</>
                                )}
                            </Button>
                        </div>
                    )}
                </div>
                
                <p className="text-center text-sm text-gray-500 mt-6">
                    Your answers are used to generate your AI Talent Card. Be authentic — there are no right or wrong answers.
                </p>
            </main>
            <PageFooter />
        </div>
    );
};

export default AssessmentWizard;
